/**
 * MVB candidate-selection register + evidence-pack drawer + the
 * second-line challenge / dual sign-off ceremony (P112).
 *
 * Admin-only. Mirrors the IBS admission view's shape: a register table,
 * expand-a-row to reveal the evidence pack (8 scored criteria with
 * anchor bands + a manual override for substitutability, the one criterion
 * with no data source, both gate outcomes), and the governance ceremony.
 */
import { useSeatHolders } from "shared/api/useSeatHolders";
import { useState } from "react";
import { Explainer } from "shared/components/Explainer";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { ApiError } from "shared/api/client";
import { refusalMessage } from "shared/api/refusalMessage";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listOf } from "shared/i18n/listOf";
import { mvbKeys } from "shared/api/queryClient";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { holdsSeat, useMe } from "shared/api/useMe";
import type {
  MvbArchitectureReview,
  MvbCandidateRow,
  MvbPack,
  MvbTier,
} from "shared/api/types";
import { formatDate } from "shared/time/formatDate";
import { canReadCeremony, canRunOperations, canSignAs } from "shared/api/authority";
import { useJourneySpineStatus } from "shared/journey/useJourney";
import { useToast } from "shared/toast/ToastProvider";
import {
  useChallengePack,
  useDeriveMvb,
  useMvbCandidate,
  useMvbCandidates,
  useMarkConvergenceReviewed,
  useMvbConvergence,
  useOverrideScores,
  useScoreCandidate,
  useSignoffPack,
} from "twin/api/mvb_candidate_hooks";

import { SignatureHistory } from "shared/components/SignatureHistory";
// ── Static rubric metadata (labels + 0-3 anchor bands) ──────────────

interface CriterionMeta {
  key: string;
  label: string;
  manual: boolean;
  anchors: [string, string, string, string];
}

const CRITERIA: CriterionMeta[] = [
  {
    key: "customer_reach",
    label: "Customer reach",
    // P326: derived from IBSService.customer_volume (was a manual criterion).
    manual: false,
    anchors: ["<10k", "10k-100k", "100k-1m", ">=1m"],
  },
  {
    key: "financial_throughput",
    label: "Financial throughput",
    manual: false,
    anchors: ["<SAR 10m", "SAR 10m-100m", "SAR 100m-1bn", ">=SAR 1bn"],
  },
  {
    key: "recovery_evidence",
    label: "Recovery evidence",
    manual: false,
    anchors: ["0 breaches", "1 breach", "2-3 breaches", ">=4 breaches"],
  },
  {
    key: "dependency_density",
    label: "Dependency density",
    manual: false,
    anchors: ["0-1 IBS", "2-4 IBS", "5-9 IBS", ">=10 IBS"],
  },
  {
    key: "blast_radius",
    label: "Blast radius",
    manual: false,
    anchors: ["<2 downstream", "2-4", "5-9", ">=10"],
  },
  {
    key: "vendor_concentration",
    label: "Vendor concentration",
    manual: false,
    anchors: ["<25%", "25-49%", "50-79%", ">=80%"],
  },
  {
    key: "substitutability",
    label: "Substitutability",
    manual: true,
    anchors: ["Reliable workaround", "Partial", "Untested alt", "No workaround"],
  },
  {
    key: "regulatory_trace",
    label: "Regulatory trace",
    manual: false,
    anchors: ["No obligation", "1 obligation", "2-3", ">=4"],
  },
];

const MANUAL_KEYS = ["substitutability"];

const TIER_TONE: Record<MvbTier, string> = {
  core_mvb: "bg-indigo-600 text-white",
  mvb: "bg-indigo-100 text-indigo-800",
  watchlist: "bg-amber-100 text-amber-800",
  out_of_scope: "bg-slate-100 text-slate-600",
};

const TIER_LABEL: Record<MvbTier, string> = {
  core_mvb: "Core MVB",
  mvb: "MVB",
  watchlist: "Watchlist",
  out_of_scope: "Out of scope",
};

const DRIFT_TONE: Record<string, string> = {
  promoted: "text-emerald-700",
  demoted: "text-rose-700",
  stable: "text-slate-500",
  new: "text-slate-500",
};

const BRASS_BTN =
  "rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy " +
  "hover:bg-brand-brassDark disabled:opacity-50";

function TierBadge({ tier }: { tier: MvbTier | null }): JSX.Element {
  if (!tier) {
    return <span className="text-slate-400">--</span>;
  }
  return (
    <span
      data-testid="mvb-tier"
      data-tier={tier}
      className={`rounded px-2 py-0.5 text-xs font-semibold ${TIER_TONE[tier]}`}
    >
      {TIER_LABEL[tier]}
    </span>
  );
}

// ── Evidence-pack detail (scores + gates + manual override) ─────────

function PackDetail({
  candidateId,
  pack,
  isFunction = false,
}: {
  candidateId: string;
  pack: MvbPack;
  /** Round 17 L6 -- a Critical Internal Function is not customer-facing. */
  isFunction?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const rescore = useScoreCandidate();
  const override = useOverrideScores();
  const [manual, setManual] = useState<Record<string, number>>({});

  function manualValue(key: string): number {
    return manual[key] ?? pack.scores[key]?.value ?? 0;
  }

  function applyManual(): void {
    const body: Record<string, number> = {};
    for (const key of MANUAL_KEYS) {
      if (manual[key] !== undefined) body[key] = manual[key];
    }
    override.mutate(
      { candidateId, body },
      {
        onSuccess: () =>
          toast.push({ tone: "success", message: "Manual scores applied" }),
        onError: (e) =>
          toast.push({ tone: "error", message: "Override failed", detail: e.detail }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="mvb-pack-detail">
      {/* Scores grid */}
      <div data-testid="mvb-scores" className="grid grid-cols-1 gap-1 md:grid-cols-2">
        {CRITERIA.map((c) => {
          const value = c.manual
            ? manualValue(c.key)
            : (pack.scores[c.key]?.value ?? 0);
          return (
            <div
              key={c.key}
              data-testid={`mvb-score-${c.key}`}
              className="flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-sm"
            >
              <span className="text-slate-700">
                {c.label}
                {/* Round 16 H27 -- "SubstitutabilityMANUAL", "1 of 3--
                    10k-100k" and "Gate 1Continuity-critical" in the text:
                    each piece now has a space of its own. */}
                {c.manual && " "}
                {c.manual && (
                  <span className="ms-1 text-[10px] text-slate-400">
                    {/* Round 19 H55 -- "Substitutability MANUAL", shouted. */}
                    {t("screens.mvbCandidatesView.manual")}
                  </span>
                )}
                {c.manual &&
                  (pack.scores[c.key]?.data_extract ?? "").includes("pending BC") &&
                  " "}
                {c.manual &&
                  (pack.scores[c.key]?.data_extract ?? "").includes("pending BC") && (
                    <span
                      data-testid={`mvb-pending-bc-${c.key}`}
                      title={t("screens.mvbCandidatesView.defaultsToWorstCaseUntil")}
                      className="ms-1 rounded bg-amber-100 px-1 text-[10px] uppercase text-amber-700"
                    >
                      {t("screens.mvbCandidatesView.pendingBc")}
                    </span>
                  )}
              </span>{" "}
              {/* A4 -- and the field itself, not only the button. A
                  reviewer editing a score he cannot apply is worse than
                  one who was never offered the control: he believes he
                  has recorded something. */}
              {c.manual && canRunOperations(me.data) ? (
                <select
                  data-testid={`mvb-manual-${c.key}`}
                  value={value}
                  onChange={(e) =>
                    setManual((m) => ({ ...m, [c.key]: Number(e.target.value) }))
                  }
                  className="rounded border border-slate-300 text-sm"
                >
                  {[0, 1, 2, 3].map((n) => (
                    <option key={n} value={n}>
                      {n} -- {c.anchors[n]}
                    </option>
                  ))}
                </select>
              ) : (
                // F -- the score and its band were separated by a
                // MARGIN, so "1" beside "10k-100k" read as "110k-100k":
                // one number, and a nonsense one. The editable branch
                // beside it already spells the separator out.
                <span className="text-slate-600">
                  <span className="font-semibold">
                    {t("modules.twin.mvbCandidates.scoreOfThree", { value })}
                  </span>{" "}
                  <span className="ms-1 text-xs text-slate-400">
                    {"-- "}
                    {c.anchors[value]}
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Gates */}
      <div className="flex flex-col gap-1 text-sm">
        <p data-testid="mvb-gate1">
          <span
            className={`me-2 rounded px-1.5 py-0.5 text-xs font-semibold ${
              pack.gate1_continuity_critical
                ? "bg-rose-100 text-rose-800"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            {t("screens.mvbCandidatesView.gate1")}
          </span>{" "}
          {pack.gate1_reason}
        </p>
        <p data-testid="mvb-gate2">
          <span
            className={`me-2 rounded px-1.5 py-0.5 text-xs font-semibold ${
              pack.gate2_external
                ? "bg-indigo-100 text-indigo-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {t("screens.mvbCandidatesView.gate2")}
          </span>{" "}
          {pack.gate2_reason}
          {/* Round 17 L6 -- Treasury settlement read "Externally
              observable" with nothing saying what that means for a
              function no customer sees. */}
          {isFunction && (
            <span data-testid="mvb-gate2-function" className="ms-1 text-slate-500">
              {t("screens.mvbCandidatesView.gate2Function")}
            </span>
          )}
        </p>
      </div>

      {/* A4 -- scoring is the resilience desk's work. The reviewer was
          offered "Apply manual scores" and "Re-score from signals" on a
          page he is here to CHALLENGE; changing the scores is the one
          thing a second-line reviewer must not do to the thing he is
          reviewing. `canRunOperations` is the same predicate the
          endpoints behind these buttons already use. */}
      {canRunOperations(me.data) && (
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="mvb-apply-manual"
            className={BRASS_BTN}
            disabled={override.isPending}
            onClick={applyManual}
          >
            {t("screens.mvbCandidatesView.applyManualScores")}
          </button>
          <button
            type="button"
            data-testid="mvb-rescore"
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm text-brand-brassDark"
            disabled={rescore.isPending}
            onClick={() =>
              rescore.mutate(
                { candidateId },
                {
                  onSuccess: () =>
                    toast.push({ tone: "info", message: "Re-scored from signals" }),
                },
              )
            }
          >
            {t("screens.mvbCandidatesView.reScoreFromSignals")}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Ceremony: challenge + dual sign-off ─────────────────────────────

function CeremonyPanel({ pack }: { pack: MvbPack }): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  // Round 17 G19 -- the reviewer the challenge waits on, by name.
  const seats = useSeatHolders(!pack.challenge_at);
  const challenge = useChallengePack();
  const signoff = useSignoffPack();
  const me = useMe();
  // Only the roles this session actually holds. `canSignAs` deliberately
  // does not admit admin -- a signature is the signer's own.
  const signableRoles = (["cro", "ae"] as const).filter((r) => canSignAs(me.data, r));
  // A2 -- and the challenge is the reviewer's, for the same reason the
  // signature is the signer's own. This form rendered for anyone who
  // could reach the register, so the head of ops res recorded the
  // second line's opinion under her name.
  const isReviewer = holdsSeat(me.data, "reviewer");
  // A5 -- the reviewer and the signer are the session. Only the note
  // is typed, so the register records the same actor the inbox does.
  const [cNote, setCNote] = useState("");

  if (pack.workflow_state === "approved") {
    return (
      <div className="flex flex-col gap-2">
        <p
          data-testid="mvb-approved"
          className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {t("screens.mvbCandidatesView.approved")}
        </p>
        {/* C1 -- the same signatures block the admission register got.
            This ran three names together in one sentence and quoted
            none of the three comments the ceremony had collected, so
            the record of who challenged the pack and why was on the
            screen and unreadable. */}
        <SignatureHistory
          testid="mvb-pack-signatures"
          entries={[
            {
              roleLabel: t("roles.challenge"),
              name: pack.challenge_reviewer_name ?? null,
              at: pack.challenge_at ?? null,
              note: pack.challenge_note ?? null,
            },
            {
              roleLabel: t("roles.cro"),
              name: pack.cro_signoff_name ?? null,
              at: pack.cro_signoff_at ?? null,
              note: pack.cro_signoff_note ?? null,
              // Round 10 F2 -- the sign buttons send a role and
              // nothing else; only the challenge form takes words.
              commentOffered: false,
            },
            {
              roleLabel: t("roles.ae"),
              name: pack.ae_signoff_name ?? null,
              at: pack.ae_signoff_at ?? null,
              note: pack.ae_signoff_note ?? null,
              commentOffered: false,
            },
          ]}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="mvb-ceremony">
      {!pack.challenge_at && !isReviewer ? (
        <p
          data-testid="mvb-awaiting-reviewer"
          className="rounded border border-slate-200 px-3 py-2 text-xs text-slate-500"
        >
          {(seats.data?.holders?.reviewer ?? []).length === 1
            ? t("modules.twin.ibsAdmission.challenge.awaitingReviewerNamed", {
                who: seats.data?.holders?.reviewer?.[0] ?? "",
              })
            : t("modules.twin.ibsAdmission.challenge.awaitingReviewer")}
        </p>
      ) : !pack.challenge_at ? (
        <form
          data-testid="mvb-challenge-form"
          className="flex flex-col gap-2 rounded border border-slate-200 p-2"
          onSubmit={(e) => {
            e.preventDefault();
            challenge.mutate(
              {
                packId: pack.id,
                body: { note: cNote },
              },
              {
                onSuccess: () =>
                  toast.push({
                    tone: "success",
                    message: "Second-line challenge recorded",
                  }),
                onError: (err) =>
                  toast.push({
                    tone: "error",
                    message: "Challenge failed",
                    detail: err.detail,
                  }),
              },
            );
          }}
        >
          <p className="text-xs font-semibold uppercase text-slate-500">
            {t("modules.twin.mvbCandidates.ceremony.challengeLabel")}
          </p>
          <p data-testid="mvb-challenge-as" className="text-xs text-slate-500">
            {t("modules.twin.mvbCandidates.ceremony.recordingAs", {
              name: me.data?.display_name ?? me.data?.username ?? "",
            })}
          </p>
          <input
            dir="auto"
            placeholder={t("modules.twin.mvbCandidates.ceremony.challengeNote")}
            value={cNote}
            onChange={(e) => setCNote(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <button
            type="submit"
            data-testid="mvb-challenge-submit"
            className={BRASS_BTN}
            disabled={challenge.isPending}
          >
            {t("modules.twin.mvbCandidates.ceremony.recordChallenge")}
          </button>
        </form>
      ) : (
        <p className="text-sm text-slate-600" data-testid="mvb-challenged-by">
          {t("screens.mvbCandidatesView.challengedBy")} {pack.challenge_reviewer_name}.
        </p>
      )}

      {pack.challenge_at && (
        <div
          data-testid="mvb-signoff"
          className="flex flex-col gap-2 rounded border border-slate-200 p-2"
        >
          <p className="text-xs font-semibold uppercase text-slate-500">
            {t("modules.twin.mvbCandidates.ceremony.dualSignoff")}
          </p>
          <p className="text-sm">
            {t("screens.mvbCandidatesView.cro2")}{" "}
            {pack.cro_signoff_name ?? (
              <span className="text-slate-400">
                {t("screens.mvbCandidatesView.pending")}
              </span>
            )}
            {" · "}
            {t("screens.mvbCandidatesView.ae2")}{" "}
            {pack.ae_signoff_name ?? (
              <span className="text-slate-400">
                {t("screens.mvbCandidatesView.pending")}
              </span>
            )}
          </p>
          {Boolean(signableRoles.length) && (
            <p data-testid="mvb-signoff-as" className="text-xs text-slate-500">
              {t("modules.twin.mvbCandidates.ceremony.signingAs", {
                name: me.data?.display_name ?? me.data?.username ?? "",
              })}
            </p>
          )}
          {/* A2: these were rendered for anyone who could open the
              page, so the Resilience Lead was offered "Sign as CRO",
              pressed it, and got a refusal that read "already
              signed?". Offer a signature only to the session that can
              give it; tell everyone else who is being waited on and
              where they sign. */}
          {!signableRoles.length && (
            <p data-testid="mvb-signoff-waiting" className="text-sm text-slate-600">
              {t("modules.twin.mvbCandidates.ceremony.waitingOnSigners")}
            </p>
          )}
          <div className="flex gap-2">
            {signableRoles.map((role) => (
              <button
                key={role}
                type="button"
                data-testid={`mvb-signoff-${role}`}
                className={BRASS_BTN}
                disabled={
                  signoff.isPending ||
                  (role === "cro"
                    ? Boolean(pack.cro_signoff_at)
                    : Boolean(pack.ae_signoff_at))
                }
                onClick={() =>
                  signoff.mutate(
                    { packId: pack.id, body: { role } },
                    {
                      onSuccess: (p) =>
                        toast.push({
                          tone: p.workflow_state === "approved" ? "success" : "info",
                          message:
                            p.workflow_state === "approved"
                              ? "Approved -- both sign-offs recorded"
                              : `${role.toUpperCase()} sign-off recorded`,
                        }),
                      onError: (err) =>
                        toast.push({
                          tone: "error",
                          message: "Sign-off failed",
                          detail: err.detail,
                        }),
                    },
                  )
                }
              >
                {t("modules.twin.mvbCandidates.ceremony.signAs", {
                  role: role.toUpperCase(),
                })}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stage-4 convergence panel ───────────────────────────────────────

function ArchitectureReviewNote({
  review,
}: {
  review: MvbArchitectureReview;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const mark = useMarkConvergenceReviewed();
  const date = formatDate(review.changed_at, i18n.language);
  if (!review.stale) {
    return (
      <p data-testid="mvb-architecture-reviewed" className="text-xs text-slate-600">
        {t("modules.twin.mvbCandidates.convergence.architectureReviewed", { date })}
      </p>
    );
  }
  return (
    <div
      data-testid="mvb-architecture-changed"
      role="status"
      className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
    >
      <span>
        {t("modules.twin.mvbCandidates.convergence.architectureChanged", {
          date,
          file: review.filename,
        })}
      </span>
      <button
        type="button"
        data-testid="mvb-architecture-mark-reviewed"
        disabled={mark.isPending}
        onClick={() => mark.mutate()}
        className="ms-auto rounded bg-brand-brass px-2 py-0.5 font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {t("modules.twin.mvbCandidates.convergence.markReviewed")}
      </button>
    </div>
  );
}

function ConvergencePanel(): JSX.Element {
  const { t } = useTranslation();
  const conv = useMvbConvergence(true);
  if (conv.isLoading) {
    return (
      <p className="text-sm text-slate-500">
        {t("modules.twin.mvbCandidates.convergence.loading")}
      </p>
    );
  }
  if (!conv.data) {
    return (
      <p className="text-sm text-rose-700">
        {t("modules.twin.mvbCandidates.convergence.error")}
      </p>
    );
  }
  const c = conv.data;
  return (
    <div
      data-testid="mvb-convergence"
      className="flex flex-col gap-3 rounded border border-indigo-200 bg-indigo-50/40 p-3 text-sm"
    >
      {c.architecture_review && (
        <ArchitectureReviewNote review={c.architecture_review} />
      )}
      <p data-testid="mvb-convergence-summary" className="text-slate-700">
        <span className="font-semibold">
          {t("modules.twin.mvbCandidates.convergence.proposedSet")}
        </span>{" "}
        {c.summary.proposed_count} ({c.summary.proposed_ibs_count}{" "}
        {t("screens.mvbCandidatesView.ibs")} {c.summary.proposed_service_count}{" "}
        {t("screens.mvbCandidatesView.servicesClosureGaps")}{" "}
        <span className={c.summary.closure_gap_count ? "text-rose-700" : ""}>
          {c.summary.closure_gap_count}
        </span>{" "}
        {t("screens.mvbCandidatesView.journeysCovered")} {c.journeys_covered}/
        {c.journeys_total}
      </p>

      <div data-testid="mvb-reconciliation" className="text-slate-600">
        <span className="font-semibold">
          {t("modules.twin.mvbCandidates.convergence.reconciliation")}
        </span>{" "}
        {c.reconciliation.aligned_count} {t("screens.mvbCandidatesView.aligned")}{" "}
        {c.reconciliation.pending_approval.length}{" "}
        {t("screens.mvbCandidatesView.pendingApproval")}{" "}
        {c.reconciliation.demotion_candidates.length}{" "}
        {t("screens.mvbCandidatesView.demotionCandidates")}
        {c.reconciliation.pending_approval.length > 0 && (
          <p className="mt-1 text-xs text-amber-700">
            {t("screens.mvbCandidatesView.pending2")}{" "}
            {c.reconciliation.pending_approval.join(", ")}
          </p>
        )}
        {c.reconciliation.demotion_candidates.length > 0 && (
          <p className="mt-1 text-xs text-rose-700">
            {t("screens.mvbCandidatesView.demotion")}{" "}
            {c.reconciliation.demotion_candidates.join(", ")}
          </p>
        )}
        {/* P272.2 (UX-2 J2-7): this panel COMPUTES the reconciliation
            against the formal declarations but linked nowhere -- the
            candidates<->declarations split was mutually blind. Link across. */}
        <p className="mt-2">
          <Link
            to="/foundation/mvb"
            data-testid="mvb-convergence-declarations-link"
            className="font-medium text-brand-brassDark hover:underline"
          >
            {t("modules.twin.mvbCandidates.convergence.declarationsLink")}
          </Link>
        </p>
      </div>

      {c.closure_gaps.length > 0 && (
        <div data-testid="mvb-closure-gaps">
          <p className="text-xs font-semibold uppercase text-slate-500">
            {t("screens.mvbCandidatesView.dependencyClosureGaps")}
          </p>
          <ul className="mt-1 list-disc ps-5 text-xs text-slate-600">
            {c.closure_gaps.slice(0, 12).map((g) => (
              <li key={`${g.ibs_id}-${g.service_id}`}>
                <span className="font-medium">{g.ibs_name}</span>{" "}
                {t("screens.mvbCandidatesView.dependsOn")} {g.service_name}{" "}
                <span className="text-slate-400">
                  ({g.service_tier ?? t("screens.mvbCandidatesView.notACandidate")})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Expandable row ──────────────────────────────────────────────────

function CandidateRow({
  candidate,
  expanded,
  onToggle,
}: {
  candidate: MvbCandidateRow;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const detail = useMvbCandidate(candidate.id, expanded);
  return (
    <>
      <tr
        data-testid="mvb-candidate-row"
        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
        onClick={onToggle}
      >
        <td className="px-2 py-2">
          <span className="font-medium text-slate-800">
            {candidate.display_name ?? candidate.entity_id}
          </span>
          {" · "}
          {/* ROUND 13 H -- the enum token with its underscore swapped
              for a space, uppercased. It was the one identifier
              reaching the reader on this register, and uppercasing it
              is what made a catalogued word read like a raw one
              everywhere else this round. */}
          <span
            data-testid={`mvb-candidate-provenance-${candidate.id}`}
            title={t(
              `modules.twin.mvbCandidates.provenanceWhy.${candidate.provenance}`,
              {
                defaultValue: "",
              },
            )}
            className="ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
          >
            {/* Round 17 L6 -- a function is named as one; Treasury
                settlement read "on the business-service register". */}
            {candidate.provenance === "ibs_register" &&
            candidate.service_class === "internal"
              ? t("modules.twin.mvbCandidates.provenance.function_register")
              : t(`modules.twin.mvbCandidates.provenance.${candidate.provenance}`, {
                  defaultValue: candidate.provenance.replace(/_/g, " "),
                })}
            {candidate.provenance === "ibs_register" &&
              candidate.admission_status &&
              candidate.admission_status !== "admitted" &&
              t("modules.twin.mvbCandidates.provenance.notYetAdmitted")}
          </span>
        </td>
        <td className="px-2 py-2">
          <TierBadge tier={candidate.current_tier} />
        </td>
        <td className="px-2 py-2 text-slate-600">{candidate.composite ?? "--"}</td>
        <td
          className="px-2 py-2 text-slate-600"
          data-testid={`mvb-recommendation-${candidate.id}`}
        >
          {/* Round 19 H55 -- it read "escalateESCALATE": the raw value
              and a badge saying it again, glued. The word, once. */}
          {candidate.recommendation
            ? t(
                `screens.mvbCandidatesView.recommendation.${candidate.recommendation}`,
                {
                  defaultValue: candidate.recommendation,
                },
              )
            : "--"}
          {candidate.escalate && candidate.recommendation !== "escalate" && (
            <>
              {" "}
              <span className="ms-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                {t("screens.mvbCandidatesView.escalate")}
              </span>
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr data-testid="mvb-candidate-detail">
          <td colSpan={4} className="bg-slate-50 px-3 py-3">
            {detail.isLoading && (
              <p className="text-sm text-slate-500">
                {t("screens.mvbCandidatesView.loadingPack")}
              </p>
            )}
            {detail.data && !detail.data.active_pack && (
              <p className="text-sm text-slate-500" data-testid="mvb-no-pack">
                {t("screens.mvbCandidatesView.noEvidencePackYetReScoreTo")}
              </p>
            )}
            {detail.data?.active_pack && (
              <div className="flex flex-col gap-3">
                <p data-testid="mvb-drift" className="text-xs">
                  {t("screens.mvbCandidatesView.drift")}{" "}
                  <span className={DRIFT_TONE[detail.data.drift]}>
                    {detail.data.drift}
                  </span>
                  {detail.data.prior_tier && (
                    <span className="text-slate-400">
                      {" "}
                      {t("screens.mvbCandidatesView.was")}{" "}
                      {TIER_LABEL[detail.data.prior_tier]})
                    </span>
                  )}
                  {" · "}
                  <span className="ms-2 text-slate-400">
                    {t("screens.mvbCandidatesView.v")}
                    {detail.data.active_pack.version} · {detail.data.active_pack.source}
                  </span>
                </p>
                <PackDetail
                  candidateId={candidate.id}
                  pack={detail.data.active_pack}
                  isFunction={candidate.service_class === "internal"}
                />
                <CeremonyPanel pack={detail.data.active_pack} />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── View shell ──────────────────────────────────────────────────────

export function MvbCandidatesView(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const isAdmin = canRunOperations(me.data);
  const candidates = useMvbCandidates();
  const derive = useDeriveMvb();

  /* A4 -- an instruction that cannot work, and an action that succeeds
     meaninglessly.

     On a bank with zero services, zero IBSes and zero capabilities the
     page said "No MVB candidates yet. Run 'Derive all' to score the
     estate." There was no estate. Derive all was enabled, carried no
     title, ran, and toasted "Derived 0 candidates - Core MVB 0 - MVB 0
     - Watchlist 0", after which the page still said to run it. The
     instruction is circular and nothing said there had been nothing to
     score.

     Intake reads IBSes, the services beneath them and material
     vendors, so "nothing to score" is an estate with no services and
     no IBSes. The journey spine is the product's own shared answer to
     what the estate holds; null while it loads, because an unknown
     count must not read as zero and turn a slow request into a
     refusal. */
  const spine = useJourneySpineStatus();
  const countOf = (key: string) =>
    spine.data?.steps.find((step) => step.key === key)?.count ?? null;
  const serviceCount = countOf("services");
  const ibsCount = countOf("ibs_register");
  const nothingToScore = serviceCount === 0 && ibsCount === 0;
  const qc = useQueryClient();
  /* Round 11 D1 -- one rule, two surfaces, and only one enforced it.
     The register disables its control with the blocker named; this
     button, three clicks away and starting the SAME action, was
     enabled with no title and could defeat it. Both ask the server the
     same question now rather than each deciding for itself. */
  const slot = useQuery({
    // Round 14 C3 -- one key for the slot, under the declaration root,
    // so a declaration made, signed or refused anywhere refreshes it.
    queryKey: mvbKeys.slot(),
    queryFn: () =>
      api.get<{
        in_progress: boolean;
        declaration_id: string | null;
        name: string | null;
        status: string | null;
        in_force_name?: string | null;
      }>("/v1/admin/twin/mvb/declaration-slot"),
  });
  const blockedBy = slot.data?.in_progress ? slot.data : null;
  const declareFromTierSet = useMutation({
    mutationFn: () =>
      api.post<{
        declaration_id: string;
        name: string;
        member_count: number;
        reused: boolean;
        open_status?: string | null;
      }>("/v1/admin/twin/mvb/declare-from-tier-set", {}),
    onSuccess: (r) => {
      // B3 -- "a draft is open" and "one is already with the CRO" ask
      // different things of the reader. Saying only "reused" is what
      // let three declarations accumulate, one of them signed.
      toast.push({
        tone: r.reused ? "info" : "success",
        message: r.reused
          ? r.open_status === "open"
            ? t("modules.twin.mvbCandidates.declareInCeremony")
            : t("modules.twin.mvbCandidates.declareReused")
          : t("modules.twin.mvbCandidates.declared", { count: r.member_count }),
        detail: r.name,
      });
      // D1 -- the draft landed and nothing on screen moved, so the
      // operator pressed the button again. The register it appears in
      // has to be told it has changed.
      void qc.invalidateQueries();
    },
    onError: (e: ApiError) => {
      toast.push({
        tone: e.status === 422 ? "warning" : "error",
        // Round 14 C6 -- the catalogue's sentence, in the reader's
        // language, naming whoever holds the slot.
        message: e.detail
          ? refusalMessage(t, e)
          : t("modules.twin.mvbCandidates.declareFailed"),
      });
      // Round 14 C3 -- a refused draft means the slot moved: re-read it,
      // so the button disables and names the declaration in the way.
      void qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
  // D7 -- the inbox row landed on the whole register: twenty-four rows,
  // and the reviewer had to find the one he had just clicked.
  const [params] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(params.get("candidate"));
  const [showConvergence, setShowConvergence] = useState(false);

  // B2 -- reading is not signing. A signer following the link from her
  // own inbox used to meet "Admin role required." on the decision
  // waiting for her signature.
  if (me.data && !canReadCeremony(me.data)) {
    return (
      <section data-testid="mvb-forbidden" className="p-6 text-slate-600">
        {t("modules.twin.mvbCandidates.forbidden")}
      </section>
    );
  }

  const rows = candidates.data?.candidates ?? [];
  // H -- how the register came to hold what it holds.
  const intakeCounts = {
    ibs_register: rows.filter(
      (c) => c.provenance === "ibs_register" && c.service_class !== "internal",
    ).length,
    // Round 17 L6 -- functions counted apart: "13 IBSes on the register"
    // counted Treasury settlement and Ledger close among them.
    functions: rows.filter(
      (c) => c.provenance === "ibs_register" && c.service_class === "internal",
    ).length,
    supporting_service: rows.filter((c) => c.provenance === "supporting_service")
      .length,
    regulator_mandated: rows.filter((c) => c.provenance === "regulator_mandated")
      .length,
  };

  // Round 18 P7 -- register rows not yet scanned, named beside the count.
  const unscannedServices = candidates.data?.unscanned_services ?? 0;
  const unscannedFunctions = candidates.data?.unscanned_functions ?? 0;
  const unscannedTotal = unscannedServices + unscannedFunctions;
  const unscannedParts = [
    unscannedServices > 0 &&
      t("modules.twin.mvbCandidates.intakeUnscannedServices", {
        count: unscannedServices,
      }),
    unscannedFunctions > 0 &&
      t("modules.twin.mvbCandidates.intakeUnscannedFunctions", {
        count: unscannedFunctions,
      }),
  ].filter((x): x is string => typeof x === "string");

  return (
    <section data-testid="mvb-candidates-view" className="flex flex-col gap-4">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500">
            {t("modules.twin.mvbCandidates.kicker")}
          </p>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="mvb"
              size={22}
              className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
            />
            {t("modules.twin.mvbCandidates.heading")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            {t("modules.twin.mvbCandidates.intro")}
          </p>
          {/* P272.2 (UX-2 J2-7): candidates -> the formal declaration
              register (the two halves of the ceremony were mutually blind). */}
          <Link
            to="/foundation/mvb"
            data-testid="mvb-candidates-declarations-link"
            className="mt-1 inline-block text-xs font-medium text-brand-brassDark hover:underline"
          >
            {t("modules.twin.mvbCandidates.convergence.declarationsLink")}
          </Link>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="mvb-convergence-toggle"
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm text-brand-brassDark"
            onClick={() => setShowConvergence((v) => !v)}
          >
            {t("modules.twin.mvbCandidates.convergenceToggle")}
          </button>
          {/* B2 -- running the derivation is the operator's act; a
              signer reads the page without being offered it. */}
          {isAdmin && (
            <button
              type="button"
              data-testid="mvb-derive-all"
              className={BRASS_BTN}
              disabled={derive.isPending || nothingToScore}
              title={
                nothingToScore
                  ? t("modules.twin.mvbCandidates.nothingToScoreTitle")
                  : undefined
              }
              onClick={() =>
                derive.mutate(undefined, {
                  onSuccess: (r) =>
                    toast.push(
                      // A4 -- "Derived 0 candidates" reads as a
                      // completed piece of work. Nothing to score is a
                      // different outcome and says so.
                      r.candidates === 0
                        ? {
                            tone: "info",
                            message: t("modules.twin.mvbCandidates.derivedNothing"),
                            detail: t(
                              "modules.twin.mvbCandidates.derivedNothingDetail",
                            ),
                          }
                        : {
                            tone: "success",
                            message: `Derived ${r.candidates} candidates`,
                            detail: `Core MVB ${r.by_tier.core_mvb ?? 0} · MVB ${r.by_tier.mvb ?? 0} · Watchlist ${r.by_tier.watchlist ?? 0}`,
                          },
                    ),
                  onError: (e) =>
                    toast.push({
                      tone: "error",
                      message: "Derive failed",
                      detail: e.detail,
                    }),
                })
              }
            >
              {t("modules.twin.mvbCandidates.deriveAll")}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              data-testid="mvb-declare-from-tier-set"
              className={BRASS_BTN}
              disabled={declareFromTierSet.isPending || blockedBy !== null}
              title={
                blockedBy
                  ? t("modules.twin.mvbCandidates.declareBlocked", {
                      name: blockedBy.name ?? "",
                    })
                  : undefined
              }
              onClick={() => declareFromTierSet.mutate()}
            >
              {t("modules.twin.mvbCandidates.declareFromTierSet")}
            </button>
          )}
          {isAdmin && !blockedBy && slot.data?.in_force_name && (
            <p
              data-testid="mvb-declare-supersedes"
              className="w-full text-xs text-slate-600"
            >
              {t("modules.twin.mvbCandidates.declareSupersedes", {
                name: slot.data.in_force_name,
              })}
            </p>
          )}
        </div>
      </header>

      <Explainer termKey="mvb" className="max-w-3xl" />

      {showConvergence && <ConvergencePanel />}

      {candidates.isLoading && (
        <p className="text-sm text-slate-500">
          {t("modules.twin.mvbCandidates.loading")}
        </p>
      )}
      {candidates.isError && (
        <p className="text-sm text-rose-700">{t("modules.twin.mvbCandidates.error")}</p>
      )}

      {/* ROUND 13 H, eighth round -- the register does not contain the
          reader's own service and does not say why.
          
          It holds what the intake FOUND, by three rules, and named
          none of them -- so a man who could not find his business
          service had no way to tell whether it had been judged and
          left out, or never looked at. The counts and the rules are
          the answer; the rule that excludes his is the last line. */}
      {!candidates.isLoading && rows.length > 0 && (
        <p
          data-testid="mvb-intake-summary"
          className="max-w-3xl text-xs text-slate-600"
        >
          {t("modules.twin.mvbCandidates.intakeSummary", {
            total: rows.length,
            ibses: intakeCounts.ibs_register,
            functions: intakeCounts.functions,
            services: intakeCounts.supporting_service,
            vendors: intakeCounts.regulator_mandated,
          })}{" "}
          {unscannedParts.length > 0 && (
            <span data-testid="mvb-intake-unscanned">
              {t("modules.twin.mvbCandidates.intakeUnscanned", {
                parts: listOf(unscannedParts),
                verb: t("modules.twin.mvbCandidates.intakeUnscannedVerb", {
                  count: unscannedTotal,
                }),
              })}{" "}
            </span>
          )}
          <span className="text-slate-500">
            {t("modules.twin.mvbCandidates.intakeExcludes")}
          </span>
        </p>
      )}

      {!candidates.isLoading && rows.length === 0 ? (
        <p data-testid="mvb-empty" className="text-sm text-slate-500">
          {t(
            nothingToScore
              ? "modules.twin.mvbCandidates.emptyNoEstate"
              : "modules.twin.mvbCandidates.empty",
          )}
        </p>
      ) : (
        <table data-testid="mvb-candidates-table" className="w-full text-start text-sm">
          <thead className="text-[11px] uppercase text-slate-400">
            <tr>
              <th className="px-2 py-1">
                {t("modules.twin.mvbCandidates.col.candidate")}
              </th>
              <th className="px-2 py-1">{t("modules.twin.mvbCandidates.col.tier")}</th>
              <th className="px-2 py-1">
                {t("modules.twin.mvbCandidates.col.composite")}
              </th>
              <th className="px-2 py-1">
                {t("modules.twin.mvbCandidates.col.recommendation")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                expanded={expanded === c.id}
                onToggle={() => setExpanded((cur) => (cur === c.id ? null : c.id))}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
